import React from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { handleError } from '../../common/utils/util.errors';
import { Stack, LinearProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton } from '@mui/material';
import { Button, Typography } from 'components/index';
import { bulkUpdateLineItems, getLineItem, updateLineItem } from './glossary.api';
import SelectDivision from './SelectDivision';
import SelectInput from '../SelectInput';
import { toast } from 'react-toastify';
import { LineItem, LineItemUnit, LineItemUnitEnum } from './glossary.type';
import { Close } from '@mui/icons-material';

interface GlossaryDialogProps {
  open: boolean;
  onClose: () => void;
  lineItemId?: string | null;
  lineItems?: LineItem[] | null | undefined; // new prop for bulk edit
  bulkEdit?: boolean;
}

const GlossaryDialog: React.FC<GlossaryDialogProps> = ({ open, onClose, lineItemId, lineItems = null, bulkEdit = false }) => {
  const [selectedDivision, setSelectedDivision] = React.useState<string | null>(null);
  const [selectedUnit, setSelectedUnit] = React.useState<LineItemUnit>(null);
  const [isUpdating, setIsUpdating] = React.useState(false);
  const queryClient = useQueryClient();

  const isBulkEdit = bulkEdit && lineItems && lineItems.length > 0;

  const {
    data: lineItem,
    error,
    isError,
    isLoading,
    refetch: refetchLineItem,
  } = useQuery(
    ['lineItem', lineItemId],
    () => {
      // Ensure lineItemId is not null or undefined before calling getLineItem
      if (lineItemId) {
        return getLineItem(lineItemId);
      }
      return null;
    },
    {
      enabled: !!lineItemId && !isBulkEdit,
    },
  );

  React.useEffect(() => {
    if (isBulkEdit && lineItems) {
      setSelectedDivision(lineItems[0].divisionId);
      setSelectedUnit(lineItems[0].unit);
    } else if (!isBulkEdit && lineItem) {
      setSelectedDivision(lineItem.divisionId);
      setSelectedUnit(lineItem.unit);
    }
  }, [isBulkEdit, lineItems, lineItem]);

  if (isError) handleError(error);

  const unitsList = [...Object.values(LineItemUnitEnum).map(unit => ({ value: unit, label: unit })), { label: 'None', value: '' }];
  const canUpdate = lineItem?.divisionId !== selectedDivision || lineItem?.unit !== selectedUnit;

  const handleUpdate = async () => {
    if (isBulkEdit) {
      // Construct payload for bulk update
      try {
        setIsUpdating(true);

        // Prepare the payload for bulk update
        const updatedLineItems = lineItems.map(item => ({
          ...item,
          divisionId: selectedDivision,
          unit: selectedUnit,
        }));

        // Call the bulk update API
        //if (selectedDivision) {
        await bulkUpdateLineItems(updatedLineItems, selectedDivision);

        toast.success('Line items updated successfully', { autoClose: 1000 });
        //}
        // Optionally refetch data or perform other actions
        onClose();
      } catch (error) {
        handleError(error);
      } finally {
        setIsUpdating(false);
      }
      // Call the bulk update API
    } else if (lineItem) {
      // Existing update logic for a single line item
      if (!canUpdate) return;
      const updatedLineItem = {
        ...lineItem,
        unit: selectedUnit,
        divisionId: selectedDivision,
      };
      try {
        setIsUpdating(true);
        await updateLineItem(lineItem.id, updatedLineItem);
        toast.success('Line Item updated successfully', { autoClose: 1000 });
        refetchLineItem();
        onClose();
      } catch (error) {
        handleError(error);
      } finally {
        setIsUpdating(false);
      }
    }
  };

  if (!lineItemId && !lineItems) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center' }}>
        Glossary
        <IconButton onClick={onClose} sx={{ marginLeft: 'auto' }}>
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          {lineItem ? (
            <Typography pt={2}>
              <b>Line Item:</b> {lineItem?.description}
            </Typography>
          ) : (
            <></>
          )}
          {isLoading ? (
            <LinearProgress />
          ) : (
            <>
              <SelectDivision selectedDivision={selectedDivision} setSelectedDivision={setSelectedDivision} />
              <SelectInput
                label='Unit'
                selected={selectedUnit ?? ''}
                setSelected={newVal => {
                  setSelectedUnit(newVal === '' ? null : (newVal as LineItemUnitEnum));
                }}
                list={unitsList}
                variant='standard'
              />
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} variant='outlined' color='error'>
          Cancel
        </Button>
        <Button disabled={!canUpdate} onClick={handleUpdate} variant='contained' loading={isUpdating}>
          Update
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default GlossaryDialog;
